import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import './CreateCampaign.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateCampaign = () => {
  const navigate = useNavigate();
  const { user, sessionToken } = useAuth();

  const [step, setStep] = useState(1); // 1: Select Brand, 2: Select Consumers, 3: Select Products, 4: Preview & Send

  // Step 1: Brand Selection
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // Step 2: Consumer Selection
  const [consumers, setConsumers] = useState([]);
  const [selectedConsumers, setSelectedConsumers] = useState([]);
  const MAX_CONSUMERS = 25;

  // Step 3: Product Selection
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const MAX_PRODUCTS = 4;

  // Campaign Details
  const [campaignName, setCampaignName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generated Try-ons
  const [generatedTryons, setGeneratedTryons] = useState([]);
  const [campaignId, setCampaignId] = useState(null);

  useEffect(() => {
    if (user && user.is_merchant) {
      loadBrands();
    }
  }, [user]);

  const loadBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setBrands(response.data.brands || []);
    } catch (err) {
      console.error('Error loading brands:', err);
    }
  };

  const loadConsumers = async (brandId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/brands/${brandId}/favorited-by`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setConsumers(response.data.consumers || []);
    } catch (err) {
      console.error('Error loading consumers:', err);
      setError('Failed to load consumers');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async (brandId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products?brand=${brandId}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setProducts(response.data.products || []);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    loadConsumers(brand.id);
    loadProducts(brand.id);
    setStep(2);
  };

  const toggleConsumer = (consumer) => {
    const isSelected = selectedConsumers.find(c => c.id === consumer.id);
    
    if (isSelected) {
      setSelectedConsumers(selectedConsumers.filter(c => c.id !== consumer.id));
    } else if (selectedConsumers.length < MAX_CONSUMERS) {
      setSelectedConsumers([...selectedConsumers, consumer]);
    } else {
      alert(`Maximum ${MAX_CONSUMERS} consumers allowed per campaign`);
    }
  };

  const toggleProduct = (product) => {
    const isSelected = selectedProducts.find(p => p.id === product.id);
    
    if (isSelected) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else if (selectedProducts.length < MAX_PRODUCTS) {
      setSelectedProducts([...selectedProducts, product]);
    } else {
      alert(`Maximum ${MAX_PRODUCTS} products allowed per consumer`);
    }
  };

  const handleCreateAndGenerate = async () => {
    if (!campaignName.trim()) {
      alert('Please enter a campaign name');
      return;
    }

    if (selectedConsumers.length === 0) {
      alert('Please select at least one consumer');
      return;
    }

    if (selectedProducts.length === 0) {
      alert('Please select at least one product');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Step 1: Create campaign
      const formData = new FormData();
      formData.append('brand_id', selectedBrand.id);
      formData.append('campaign_name', campaignName);
      formData.append('consumer_ids', JSON.stringify(selectedConsumers.map(c => c.id)));
      formData.append('product_ids', JSON.stringify(selectedProducts.map(p => p.id)));

      const createResponse = await axios.post(
        `${API}/merchant/campaigns/create`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (createResponse.data.success) {
        const newCampaignId = createResponse.data.campaign_id;
        setCampaignId(newCampaignId);

        // Step 2: Generate try-ons
        const generateResponse = await axios.post(
          `${API}/merchant/campaigns/${newCampaignId}/generate-tryons`,
          {},
          {
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          }
        );

        if (generateResponse.data.success) {
          setGeneratedTryons(generateResponse.data.tryons);
          setStep(4);
          alert(`Campaign created! Generated ${generateResponse.data.generated_count} try-on images`);
        }
      }

    } catch (err) {
      console.error('Campaign creation error:', err);
      setError(err.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${API}/merchant/campaigns/${campaignId}/send`,
        {},
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );

      if (response.data.success) {
        alert(`Campaign sent successfully! ${response.data.emails_sent} emails delivered`);
        navigate('/merchant');
      }

    } catch (err) {
      console.error('Campaign send error:', err);
      setError(err.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setLoading(false);
    }
  };

  if (!user || !user.is_merchant) {
    return (
      <Layout>
        <div className="access-denied">
          <h2>⚠️ Merchant Access Required</h2>
          <p>This page is only accessible to merchants.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="create-campaign-page">
        <div className="campaign-header">
          <h1>📧 Create Marketing Campaign</h1>
          <div className="campaign-steps">
            <div className={`step ${step >= 1 ? 'active' : ''}`}>1. Select Brand</div>
            <div className={`step ${step >= 2 ? 'active' : ''}`}>2. Select Consumers</div>
            <div className={`step ${step >= 3 ? 'active' : ''}`}>3. Select Products</div>
            <div className={`step ${step >= 4 ? 'active' : ''}`}>4. Review & Send</div>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            ⚠️ {error}
          </div>
        )}

        {/* Step 1: Select Brand */}
        {step === 1 && (
          <div className="step-content">
            <h2>Select Your Brand</h2>
            <div className="brands-grid">
              {brands.map(brand => (
                <div
                  key={brand.id}
                  className="brand-card"
                  onClick={() => handleBrandSelect(brand)}
                >
                  <h3>{brand.name}</h3>
                  <p>{brand.address}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Consumers */}
        {step === 2 && (
          <div className="step-content">
            <div className="step-header">
              <h2>Select Consumers</h2>
              <p>Choose up to {MAX_CONSUMERS} consumers who favorited {selectedBrand?.name}</p>
              <div className="selection-counter">
                {selectedConsumers.length} / {MAX_CONSUMERS} selected
              </div>
            </div>

            {consumers.length === 0 ? (
              <div className="empty-state">
                <p>No consumers have favorited this brand yet.</p>
              </div>
            ) : (
              <div className="consumers-grid">
                {consumers.map(consumer => {
                  const isSelected = selectedConsumers.find(c => c.id === consumer.id);
                  return (
                    <div
                      key={consumer.id}
                      className={`consumer-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleConsumer(consumer)}
                    >
                      {isSelected && <div className="selected-badge">✓</div>}
                      <h4>{consumer.name}</h4>
                      <p>{consumer.email}</p>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="step-actions">
              <button onClick={() => setStep(1)} className="btn-secondary">
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="btn-primary"
                disabled={selectedConsumers.length === 0}
              >
                Next: Select Products →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Select Products */}
        {step === 3 && (
          <div className="step-content">
            <div className="step-header">
              <h2>Select Products</h2>
              <p>Choose up to {MAX_PRODUCTS} products to showcase</p>
              <div className="selection-counter">
                {selectedProducts.length} / {MAX_PRODUCTS} selected
              </div>
            </div>

            <div className="campaign-name-input">
              <label>Campaign Name:</label>
              <input
                type="text"
                placeholder="e.g., Spring Collection 2025"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
              />
            </div>

            <div className="products-grid">
              {products.map(product => {
                const isSelected = selectedProducts.find(p => p.id === product.id);
                return (
                  <div
                    key={product.id}
                    className={`product-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleProduct(product)}
                  >
                    {isSelected && <div className="selected-badge">✓</div>}
                    <img src={product.picture_link} alt={product.product_name} />
                    <h4>{product.product_name}</h4>
                    <p className="price">${product.price}</p>
                  </div>
                );
              })}
            </div>

            <div className="step-actions">
              <button onClick={() => setStep(2)} className="btn-secondary">
                ← Back
              </button>
              <button
                onClick={handleCreateAndGenerate}
                className="btn-primary"
                disabled={loading || selectedProducts.length === 0 || !campaignName.trim()}
              >
                {loading ? 'Generating Try-Ons...' : '✨ Generate Campaign'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Review & Send */}
        {step === 4 && (
          <div className="step-content">
            <div className="step-header">
              <h2>Review & Send Campaign</h2>
              <p>Campaign: <strong>{campaignName}</strong></p>
            </div>

            <div className="campaign-summary">
              <div className="summary-item">
                <span>Brand:</span>
                <strong>{selectedBrand.name}</strong>
              </div>
              <div className="summary-item">
                <span>Consumers:</span>
                <strong>{selectedConsumers.length}</strong>
              </div>
              <div className="summary-item">
                <span>Products:</span>
                <strong>{selectedProducts.length}</strong>
              </div>
              <div className="summary-item">
                <span>Total Try-Ons Generated:</span>
                <strong>{generatedTryons.length}</strong>
              </div>
            </div>

            <div className="tryons-preview">
              <h3>Generated Try-Ons Preview</h3>
              <div className="tryons-grid">
                {generatedTryons.slice(0, 6).map((tryon, idx) => (
                  <div key={idx} className="tryon-preview-card">
                    <img src={tryon.result_image} alt={`${tryon.consumer_name} - ${tryon.product_name}`} />
                    <p>{tryon.consumer_name}</p>
                    <small>{tryon.product_name}</small>
                  </div>
                ))}
              </div>
              {generatedTryons.length > 6 && (
                <p className="more-indicator">+ {generatedTryons.length - 6} more try-ons</p>
              )}
            </div>

            <div className="step-actions">
              <button onClick={() => navigate('/merchant')} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSendCampaign}
                className="btn-primary btn-send"
                disabled={loading}
              >
                {loading ? 'Sending Emails...' : `📧 Send to ${selectedConsumers.length} Consumers`}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CreateCampaign;
